import type { SubscriptionEntityType } from 'src/api/subscriptionsApi';
import { resolveCatalogDomainLabel } from 'src/mindtime/config/subscriptionPermissionRegistry';
import type { CatalogueItem, PackageIncludedProduct, SubscriptionCycle } from './types';

const NO_DOMAIN_GROUP = 'OTHERS';
const PACKAGES_GROUP = 'PACKAGES';

const toNumberOrUndefined = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === '') return undefined;
  const parsed = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return undefined;
  return parsed;
};

export const normalizeMsrpKind = (value: unknown): SubscriptionCycle | null => {
  const raw = String(value || '')
    .trim()
    .toUpperCase();
  if (raw === 'MONTHLY') return 'monthly';
  if (raw === 'YEARLY') return 'yearly';
  return null;
};

const mapPermissionIds = (permissions: unknown): string[] =>
  Array.isArray(permissions)
    ? permissions
        .map((permission) => String(permission ?? '').trim())
        .filter(Boolean)
    : [];

const mapCatalogueItemCore = (
  item: any,
  group: any,
  groupIndex: number,
  itemIndex: number,
): CatalogueItem | null => {
  const id = String(item?.id || item?._id || '').trim();
  const label = String(item?.name || item?.label || '').trim();
  if (!id || !label) return null;

  const groupEntityType =
    String(group?.entityType || '').toUpperCase() === 'PACKAGE' ? 'PACKAGE' : 'PRODUCT';
  const entityType: SubscriptionEntityType =
    String(item?.entityType || groupEntityType).toUpperCase() === 'PACKAGE'
      ? 'PACKAGE'
      : 'PRODUCT';

  const groupKind = normalizeMsrpKind(
    group?.msrpKind || group?.groupKey || group?.groupLabel || group?.name,
  );
  const monthlyMsrp = toNumberOrUndefined(item?.monthlyMsrp);
  const yearlyMsrp = toNumberOrUndefined(item?.yearlyMsrp);
  const msrpKind =
    normalizeMsrpKind(item?.msrpKind) ||
    groupKind ||
    (monthlyMsrp != null && yearlyMsrp == null
      ? 'monthly'
      : yearlyMsrp != null && monthlyMsrp == null
        ? 'yearly'
        : null);

  return {
    id: id || `catalogue-${groupIndex}-${itemIndex}`,
    label,
    value: id,
    entityType,
    domain:
      entityType === 'PACKAGE'
        ? PACKAGES_GROUP
        : resolveCatalogDomainLabel(item?.domain, item?.solution) || NO_DOMAIN_GROUP,
    solution:
      typeof item?.solution === 'string' && item.solution.trim()
        ? item.solution.trim()
        : undefined,
    permissionIds: mapPermissionIds(item?.permissions),
    msrpKind,
  };
};

const resolvePackageIncludedProducts = (
  item: any,
  productsById: Map<string, CatalogueItem>,
): PackageIncludedProduct[] => {
  if (Array.isArray(item?.includedProducts) && item.includedProducts.length > 0) {
    return item.includedProducts
      .map((nested: any) => {
        const id = String(nested?.id || nested?._id || nested?.productId || '').trim();
        const name = String(nested?.name || nested?.label || '').trim();
        if (!id && !name) return null;
        const fromCatalogue = id ? productsById.get(id) : undefined;
        return {
          id: id || fromCatalogue?.value || name,
          name: name || fromCatalogue?.label || id,
          solution:
            (typeof nested?.solution === 'string' && nested.solution.trim()) ||
            fromCatalogue?.solution,
          permissionIds:
            mapPermissionIds(nested?.permissions).length > 0
              ? mapPermissionIds(nested?.permissions)
              : fromCatalogue?.permissionIds || [],
        } as PackageIncludedProduct;
      })
      .filter(Boolean) as PackageIncludedProduct[];
  }

  const ids = Array.isArray(item?.includedProductIds)
    ? item.includedProductIds
    : Array.isArray(item?.productIds)
      ? item.productIds
      : [];

  return ids
    .map((rawId: unknown) => {
      const id = String(rawId || '').trim();
      if (!id) return null;
      const fromCatalogue = productsById.get(id);
      return {
        id,
        name: fromCatalogue?.label || id,
        solution: fromCatalogue?.solution,
        permissionIds: fromCatalogue?.permissionIds || [],
      } as PackageIncludedProduct;
    })
    .filter(Boolean) as PackageIncludedProduct[];
};

export const mapCatalogueItems = (payload: any): CatalogueItem[] => {
  const root = payload?.data?.data ?? payload?.data ?? payload ?? {};
  const groups = Array.isArray(root?.groups)
    ? root.groups
    : Array.isArray(payload?.groups)
      ? payload.groups
      : Array.isArray(root)
        ? root
        : [];

  const productsById = new Map<string, CatalogueItem>();
  groups.forEach((group: any, groupIndex: number) => {
    const items = Array.isArray(group?.items) ? group.items : [];
    items.forEach((item: any, itemIndex: number) => {
      const mapped = mapCatalogueItemCore(item, group, groupIndex, itemIndex);
      if (!mapped || mapped.entityType !== 'PRODUCT') return;
      productsById.set(mapped.value, mapped);
    });
  });

  const mapped: CatalogueItem[] = [];
  groups.forEach((group: any, groupIndex: number) => {
    const items = Array.isArray(group?.items) ? group.items : [];
    items.forEach((item: any, itemIndex: number) => {
      const core = mapCatalogueItemCore(item, group, groupIndex, itemIndex);
      if (!core) return;

      if (core.entityType === 'PACKAGE') {
        const includedProducts = resolvePackageIncludedProducts(item, productsById);
        mapped.push({
          ...core,
          includedProducts: includedProducts.length > 0 ? includedProducts : undefined,
        });
        return;
      }

      mapped.push(core);
    });
  });

  return mapped;
};

export const cycleFromItems = (
  items: Array<{ productId: string }>,
  catalogue: CatalogueItem[],
): SubscriptionCycle | null => {
  const first = items.find((item) => item.productId);
  if (!first) return null;
  const match = catalogue.find((option) => option.value === first.productId);
  return match?.msrpKind ?? null;
};
